<i18n>
{
  "ja": {
    "title": {
      "ja": "お役立ちコンテンツ",
      "en": "Tips & Hints"
    },
    "description": "WareX（ウェアエックス）のお役立ちコンテンツ一覧ページ{page}です。倉庫利用にまつわる基礎知識やお役立ち情報をご確認いただけます。WareXは契約からご利用まで、サポート対応も充実した、新しい倉庫利用サービスです。",
    "page": "{num}ページ目",
    "search": {
      "title": "目的から探す"
    }
  }
}
</i18n>
<template>
  <div>
    <div class="mt-16 text-center w-7/8 mx-auto">
      <h1 class="block-header">{{ $t('title.ja') }}</h1>
      <p class="ornament-header">{{ $t('title.en') }}</p>
    </div>
    
    <ul class="flex justify-center mt-16">
      <li class="mx-4">
        <span class="block text-sm pb-1 border-b-4 border-secondary" >
          {{ $t('search.title') }}
        </span>
      </li>
    </ul>
    <div class="mt-8 max-w-6xl w-7/8 sm:w-5/8 mx-auto">
      <category-selector v-model="selectedCategories" :categories="categories" />
    </div>

    <div class="mt-8 max-w-6xl w-7/8 sm:w-5/8 mx-auto">
      <search-box v-model="search" />
    </div>

    <div class="bg-secondary py-8 mt-10">

      <div class="articles max-w-4xl w-7/8 sm:w-11/12 lg:w-5/8 mx-auto pt-4 flex flex-wrap justify-between">
        <template v-if="count > 0">
          <article-card class="article mb-6 sm:mb-16" v-for="(item, index) of displayedArticle" :key="index" :article="item" />
        </template>
        <div v-else class="text-center w-full">
          <p class="text-white text-lg sm:my-10">
            ご指定のキーワードでは、検索にヒットしませんでした。
          </p>
          <p class="mt-12 mb-6 sm:my-20">
            <img class="mx-auto" src="@/assets/img/error.png" srcset="@/assets/img/error@2x.png 2x, @/assets/img/error@3x.png 3x" alt="">
          </p>
        </div>
      </div>

      <pagination v-model="current" :articles="articles" :count="count" :perPage="perPage"/>
    </div>

    <BreadcrumbsFoot :list="breadcrumbs" />
  </div>
</template>

<style lang="sass" scoped>
.articles
  > .article
    width: 100%
    @screen sm
      width: 32%
  @screen sm
    &:after
      content: ''
      width: 32%
      @apply block
</style>

<script>
import mixin from "@/components/blog/archive/mixin";
import ArticleCard from "@/components/contents/articleCard";
import { getParams } from "@/components/blog/archive/functions";
import BreadcrumbsFoot from '@/components/breadcrumbsFoot';
import Jsonld from "@/components/mixins/jsonld";

const perPage = 9;
const articlesName = 'tips';
const categoryNames = [ 'purposes' ];

export default {
  meta: {
    feature: 'solutions'
  },
  mixins: [mixin, Jsonld],
  components: {
    ArticleCard,
    BreadcrumbsFoot
  },
  data() {
    return {
      breadcrumbs: [
        { name: this.$t('title.ja') } 
      ],
      perPage,
      articlesName,
      categoryNames
    }
  },
  async asyncData({ $strapi, query, app }) {
    let count, articles, categories
    try {
      const { p, c, cn, s } = query;
      const categoryName = categoryNames[cn ? cn : 0];
      const start = p ? (p - 1) * perPage : 0;
      const params = getParams( { c, s, categoryName } );
      params.push(['_start', start ])
      params.push(['_limit', perPage ])

      const tips = await $strapi.find(articlesName, params)

      count = tips.count,
      articles = tips.data,
      categories = await $strapi.find(categoryName)
    } catch (e) {
      app.$bugsnag.notify(e)
    }
    return {
      count, 
      articles, 
      categories
    }
  },
  async beforeMount() {
    if (this.params.length) {
      const tips = await this.$strapi.find(this.articlesName, this.params);
      this.articles = tips.data
      this.count = tips.count
    }
  },
  computed: {
    global() {
      return this.$store.getters['cms/global']
    },
    homepage() {
      return this.$store.getters['cms/homepage']
    }
  },
  head() {
    const { query } = this.$route
    const { p } = query
    const page = p ? this.$t('page', { num: p }) : ''
    const title = this.$t('nav.contents') + (page ? ` ${page}` : '')
    const description = this.$t('description', { page: page ? `の${page}` : '' })
    const { defaultSeo } = this.global;
    const fullSeo = {
      ...defaultSeo,
      metaTitle: title,
      metaDescription: description,
      metaKeywords: `${this.$t('nav.contents')},${this.$t('keywords')}`
    };
    const link = []
    const meta = []
    const getParams = (query) => {
      const allowQueries = ['p', 'c', 's']
      const params = []
      Object.keys(query).forEach(key => {
        if (allowQueries.includes(key)) {
          if (Array.isArray(query[key])) {
            params.push(...query[key].map(val => `${key}=${val}`))
          } else {
            params.push(`${key}=${query[key]}`)
          }
        }
      })
      return params.length ? `?${params.join('&')}` : null
    }
    const params = getParams(query)

    if (this.articles.length) {
      params && link.push({
        rel: 'canonical',
        hid: 'canonical',
        href: `${this.$seo.url(this.$route.path.replace(/^\/en/, ''))}${params}`
      })
    } else {
      meta.push({ hid: 'robots', name: 'robots', content: 'noindex, nofollow' })
    }
    return {
      title,
      link,
      meta: [
        ...meta,
        ...this.$options.filters.cmsMetaTags(fullSeo)
      ]
    };
  },
  jsonld() {
    const ret = []
    const breadcrumbs = this.getBreadcrumbsJsonld(this.breadcrumbs)
    breadcrumbs && ret.push(breadcrumbs)
    return ret
  },
};
</script>